import React, { useState, useEffect } from 'react';
import { scrollToTop } from '../utils/scrollToTop';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'projects', 'contact'];
      const viewportCenter = window.scrollY + (window.innerHeight / 2);
      
      let closestSection = 'home';
      let minDistance = Infinity;

      for (const section of sections) {
        const element = section === 'home' 
          ? document.body 
          : document.getElementById(section);
        
        if (element) {
          const offsetTop = section === 'home' ? 0 : element.offsetTop;
          const height = section === 'home' ? window.innerHeight : element.offsetHeight;
          const elementCenter = offsetTop + (height / 2);
          
          const distance = Math.abs(viewportCenter - elementCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestSection = section;
          }
        }
      }
      
      setActiveSection(closestSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); 
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href, sectionName) => {
    setIsMenuOpen(false);
    setActiveSection(sectionName);
    
    if (sectionName === 'home') {
      scrollToTop();
    } else {
      const element = document.getElementById(sectionName);
      if (element) {
        const offsetTop = element.offsetTop - 80; 
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    }
  };

  return (
    <nav className="fixed top-0 w-full bg-white/95 dark:bg-black/95 backdrop-blur-md z-50 border-b border-gray-200 dark:border-white/10 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 relative">
          {/* Logo */}
          <div className="flex-1 flex justify-start">
            <div className="text-base sm:text-lg md:text-xl font-bold text-black dark:text-white transition-colors duration-300 tracking-tight">
              &lt;90TUN/&gt;
            </div>
          </div>
          
          {/* Desktop Navigation (Centered) */}
          <div className="hidden md:flex absolute left-1/2 -translate-x-1/2">
            <div className="flex items-center space-x-8">
              {navItems.map((item) => (
                <a 
                  key={item.name} 
                  href={item.href} 
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href, item.name.toLowerCase());
                  }}
                  className={`
                    py-2 text-sm font-medium transition-all duration-300 no-underline border-b-2
                    ${activeSection === item.name.toLowerCase() 
                      ? 'text-black dark:text-white border-black dark:border-white' 
                      : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-black dark:hover:text-white'
                    }
                  `}
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          {/* Right Section (CTA + Mobile Menu) */}
          <div className="flex-1 flex justify-end items-center gap-4">
            {/* Desktop CTA */}
            <button 
              onClick={() => handleNavClick('#contact', 'contact')}
              className="hidden md:block px-5 py-2 bg-black dark:bg-white text-white dark:text-black text-sm font-medium transition-all duration-300 hover:bg-gray-800 dark:hover:bg-gray-200 hover:-translate-y-0.5"
            >
              Get In Touch
            </button>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-300 focus:outline-none"
              >
                <div className="w-6 h-6 flex flex-col justify-center items-center">
                  <div 
                    className={`
                      w-5 h-0.5 bg-black dark:bg-white mb-1 transition-all duration-300
                      ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}
                    `}
                  ></div>
                  <div 
                    className={`
                      w-5 h-0.5 bg-black dark:bg-white mb-1 transition-all duration-300
                      ${isMenuOpen ? 'opacity-0' : 'opacity-100'}
                    `}
                  ></div>
                  <div 
                    className={`
                      w-5 h-0.5 bg-black dark:bg-white transition-all duration-300
                      ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}
                    `}
                  ></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Fullscreen) */}
      <div className={`
        md:hidden fixed top-16 left-0 right-0 h-[calc(100vh-4rem)] bg-white dark:bg-black transition-all duration-300 flex flex-col items-center justify-center
        ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
      `}>
        <div className="flex flex-col items-center space-y-8 -mt-16 w-full px-6">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.href, item.name.toLowerCase());
              }}
              className={`
                text-2xl sm:text-3xl font-bold tracking-widest uppercase transition-colors duration-300 no-underline
                ${activeSection === item.name.toLowerCase() 
                  ? 'text-black dark:text-white' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white'
                }
              `}
            >
              {item.name}
            </a>
          ))}

          {/* Mobile CTA Button */}
          <button 
            onClick={() => {
              setIsMenuOpen(false);
              handleNavClick('#contact', 'contact');
            }}
            className="mt-8 w-full max-w-xs px-6 py-4 bg-black dark:bg-white text-white dark:text-black text-sm font-bold tracking-widest uppercase transition-all duration-300 hover:bg-gray-800 dark:hover:bg-gray-200"
          >
            Get In Touch
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;